import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  ApiError,
  api,
  canEdit,
  type AuthUser,
  type StrengthRequest,
} from '@/lib/api';
import { RosterTabs } from './Roster';

export default function StrengthRequests({ user }: { user: AuthUser }) {
  const editable = canEdit(user.role);
  const [creating, setCreating] = useState(false);

  const listQuery = useQuery({
    queryKey: ['strength-requests'],
    queryFn: api.listStrengthRequests,
  });

  return (
    <div className="space-y-4">
      <RosterTabs />
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Strength Requests</h1>
          <p className="text-sm text-slate-400">
            Monthly M1-M3 power collections. Compare snapshots between requests.
          </p>
        </div>
        {editable && !creating && (
          <button
            className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} /> New request
          </button>
        )}
      </header>

      {creating && editable && (
        <CreateDialog onClose={() => setCreating(false)} />
      )}

      <div className="flex flex-wrap gap-2 text-sm">
        <Link
          to="/strength-requests/stats"
          className="rounded border border-slate-700 px-3 py-1.5 hover:bg-slate-800"
        >
          Compare / Stats
        </Link>
      </div>


      {listQuery.isLoading && <div className="text-slate-400">Loading…</div>}
      {listQuery.data && listQuery.data.length === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
          No strength requests yet.
        </div>
      )}
      <ul className="space-y-2">
        {listQuery.data?.map((r) => (
          <RequestCard key={r.id} req={r} editable={editable} />
        ))}
      </ul>

      <Style />
    </div>
  );
}

function RequestCard({
  req,
  editable,
}: {
  req: StrengthRequest;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.deleteStrengthRequest(req.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['strength-requests'] }),
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to delete'),
  });
  const { submitted, pending, total } = req.counts;
  const pct = total > 0 ? (submitted / total) * 100 : 0;

  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Link
            to={`/strength-requests/${req.id}`}
            className="text-base font-medium hover:text-indigo-300"
          >
            {req.title}
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>Started {req.startedAt.slice(0, 10)}</span>
            {req.closedAt && <span>Closed {req.closedAt.slice(0, 10)}</span>}
            <span
              className={
                req.status === 'open'
                  ? 'rounded bg-emerald-700/50 px-2 py-0.5 text-emerald-100'
                  : 'rounded bg-slate-700 px-2 py-0.5 text-slate-300'
              }
            >
              {req.status}
            </span>
            <span className="tabular-nums">
              {submitted} / {total} submitted · {pending} pending
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-slate-800">
            <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {editable && (
          <button
            className="text-slate-500 hover:text-red-400 disabled:opacity-30"
            title={
              req.counts.snapshotCount > 0
                ? 'Close instead (has linked snapshots)'
                : 'Delete request'
            }
            disabled={req.counts.snapshotCount > 0}
            onClick={() => {
              if (confirm(`Delete "${req.title}"?`)) remove.mutate();
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </li>
  );
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(() => defaultTitle());
  const [startedAt, setStartedAt] = useState('');
  const [closedAt, setClosedAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createStrengthRequest({
        title: title.trim(),
        startedAt: startedAt || undefined,
        closedAt: closedAt || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['strength-requests'] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to create'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!title.trim()) return;
        if (closedAt && !startedAt) {
          setError('End date requires a start date');
          return;
        }
        create.mutate();
      }}
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3"
    >
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          required
          className="input"
        />
      </div>
      <p className="text-xs text-slate-500">
        Leave both dates empty for a fresh request starting now. Set a start
        date to backdate — existing unassigned snapshots since then will be
        pulled in. Add an end date only if the request is already finished.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
            Start date (optional)
          </label>
          <input
            type="date"
            value={startedAt}
            onChange={(e) => setStartedAt(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
            End date (optional)
          </label>
          <input
            type="date"
            value={closedAt}
            onChange={(e) => setClosedAt(e.target.value)}
            className="input"
            disabled={!startedAt}
          />
        </div>
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  );
}

function defaultTitle() {
  const d = new Date();
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `M-Abfrage ${months[d.getMonth()]} ${d.getFullYear()}`;
}


function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
